import { all, call, put, takeLatest } from 'redux-saga/effects';
import ApiInstance from 'src/helpers/apiInstance';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import { isSuccess } from 'src/containers/admin/pricing/rateEngine/rateEngineShared/apiStatus';
import { CONTRACT_PICKER_FULL_PAGE_SIZE, CONTRACT_PICKER_MAX_PAGES, CONTRACT_PICKER_PAGE_SIZE } from '../constants';
import actions from './actions';
import actionTypes from './actionTypes';
import type { INamedRef, IStateOption } from '../sharedTypes';
import type { IFetchedPage, ILoadAgenciesPayload, ILoadEnginesPayload, ILoadUnitsPayload, IPageResult } from './types';

const agenciesApi = new ApiInstance('agency_contracts/agencies/list');
const enginesApi = new ApiInstance('agency_contracts/engines/list');
const scopeUnitsApi = new ApiInstance('agency_contracts/scope_units/list');
const statesApi = new ApiInstance('states/list');
// The route pages; a country has at most a few dozen states, so one page of this size is the whole list.
const STATES_PAGE_SIZE = 99;

// One page of a list, as the server answered it, or `null` when the answer cannot be used as one.
// No error action: `apiRequest` resolves on every HTTP status, so the status check is the only handler.
function* fetchPage<T>(
  api: ApiInstance,
  search: Record<string, unknown>,
  page: number,
  pageSize: number,
): Generator<any, IFetchedPage<T> | null, any> {
  const result = yield call(sendRequest, api.get, { search: { ...search, page, page_size: pageSize } });

  if (isSuccess(result) && Array.isArray(result.data?.records)) {
    return { records: result.data.records, nextPage: result.data.pagination?.next_page ?? null };
  }

  logger.error(
    new Error(
      `[agencyContractsV2 pickers] ${api.pathTemplate} page ${page} answered ${result?.status ?? 'network error'}`,
    ),
  );

  return null;
}

// One search page of a picker list, marked for appending when it is not the first.
function* loadPage<T>(
  api: ApiInstance,
  search: Record<string, unknown>,
  page: number,
): Generator<any, IPageResult<T> | null, any> {
  const fetched = yield call(fetchPage, api, search, page, CONTRACT_PICKER_PAGE_SIZE);

  return fetched ? { ...fetched, append: page > 1 } : null;
}

// `null`, never a partial list, when the walk cannot be trusted: a short engine list would hide real engines.
function* collectAllPages(api: ApiInstance, search: Record<string, unknown>): Generator<any, INamedRef[] | null, any> {
  const collected: INamedRef[] = [];
  let page: number | null = 1;
  let pagesRead = 0;

  while (page !== null && pagesRead < CONTRACT_PICKER_MAX_PAGES) {
    const fetched: IFetchedPage<INamedRef> | null = yield call(
      fetchPage,
      api,
      search,
      page,
      CONTRACT_PICKER_FULL_PAGE_SIZE,
    );

    if (!fetched) return null;

    collected.push(...fetched.records);

    // Annotated: `page` is read back out of the answer it helped ask for, which TS reads as circular.
    const { nextPage }: { nextPage: number | null } = fetched;

    if (nextPage !== null && nextPage <= page) {
      logger.error(
        `[agencyContractsV2 pickers] ${api.pathTemplate} next_page ${nextPage} did not advance past page ${page}`,
      );

      return null;
    }

    page = nextPage;
    pagesRead += 1;
  }

  if (page !== null) {
    logger.error(
      `[agencyContractsV2 pickers] ${api.pathTemplate} stopped after ${CONTRACT_PICKER_MAX_PAGES} pages ` +
        `holding ${collected.length} records`,
    );

    return null;
  }

  return collected;
}

export function* loadAgencies({ payload }: { type: string; payload: ILoadAgenciesPayload }): Generator<any, void, any> {
  const page = payload.page ?? 1;

  try {
    const result = yield call(
      loadPage,
      agenciesApi,
      {
        ...(payload.search ? { search: payload.search } : {}),
        ...(payload.legalEntityId ? { legal_entity_id: payload.legalEntityId } : {}),
      },
      page,
    );

    yield put(result ? actions.loadAgenciesSuccess(result) : actions.loadAgenciesError(page > 1));
  } catch (error) {
    logger.error(error);

    yield put(actions.loadAgenciesError(page > 1));
  }
}

export function* loadEngines({ payload }: { type: string; payload: ILoadEnginesPayload }): Generator<any, void, any> {
  try {
    const records = yield call(
      collectAllPages,
      enginesApi,
      payload.legalEntityId ? { legal_entity_id: payload.legalEntityId } : {},
    );

    if (records) {
      yield put(actions.loadEnginesSuccess(records));

      return;
    }

    yield put(actions.loadEnginesError());
  } catch (error) {
    logger.error(error);

    yield put(actions.loadEnginesError());
  }
}

export function* loadUnits({ payload }: { type: string; payload: ILoadUnitsPayload }): Generator<any, void, any> {
  const page = payload.page ?? 1;

  try {
    const result = yield call(
      loadPage,
      scopeUnitsApi,
      {
        pricing_engine_id: payload.pricingEngineId,
        ...(payload.search ? { search: payload.search } : {}),
        ...(payload.legalEntityId ? { legal_entity_id: payload.legalEntityId } : {}),
      },
      page,
    );

    yield put(result ? actions.loadUnitsSuccess(result) : actions.loadUnitsError(page > 1));
  } catch (error) {
    logger.error(error);

    yield put(actions.loadUnitsError(page > 1));
  }
}

// A `states/list` row is `{ id, code }` — the grid emits no name — so the code labels the option too.
// A row without a code could never be sent, since the create endpoint resolves states by code.
const toStateOptions = (records: unknown[]): IStateOption[] =>
  records.flatMap(record => {
    const { id, code } = (record ?? {}) as { id?: unknown; code?: unknown };

    if (typeof id !== 'string' || typeof code !== 'string' || !code) return [];

    return [{ id, name: code, code }];
  });

export function* loadStates(): Generator<any, void, any> {
  try {
    const result = yield call(sendRequest, statesApi.get, { search: { pageSize: STATES_PAGE_SIZE } });

    // `states/list` is an old-style grid: its rows sit under `data`, not `records`, and `parseJSON` lifts that
    // array to `result.data` — dropping the key when it is empty, so a bare 2xx is a country with no states.
    if (isSuccess(result)) {
      yield put(actions.loadStatesSuccess(Array.isArray(result.data) ? toStateOptions(result.data) : []));

      return;
    }

    logger.error(
      new Error(`[agencyContractsV2 pickers] ${statesApi.pathTemplate} answered ${result?.status ?? 'network error'}`),
    );

    yield put(actions.loadStatesError());
  } catch (error) {
    logger.error(error);

    yield put(actions.loadStatesError());
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.LOAD_AGENCIES_REQUEST, loadAgencies),
    takeLatest(actionTypes.LOAD_ENGINES_REQUEST, loadEngines),
    takeLatest(actionTypes.LOAD_UNITS_REQUEST, loadUnits),
    takeLatest(actionTypes.LOAD_STATES_REQUEST, loadStates),
  ]);
}
